// The addresses typed at an activation and never proved, as values this console
// can draw.
//
// attendees.ts opens with the rule that governs this file too: every sentence
// the service sends about what a read is and is not travels through here
// untouched. Here that is `subset_note`, `use_note`, `access_note` and the
// `note` on the retention block. They are not framing around the data. On this
// read they are the only thing standing between a column of mailboxes and a
// reader who assumes a table drawn under an activation's name is that
// activation's people. The operator module says as much in its header: a table
// of email addresses will be read as a list of customers unless something on
// the page says otherwise, and the something is these strings.
//
// So they are required, in the strong sense: a response missing any of them is
// a response this console will not draw at all. That is a harder line than
// attendees.ts takes with its optional person-shaped fields, and it is the same
// line attendees.ts takes with its notes. The reasoning scales with what is
// being handed over.
//
// No policy about who may read this. The console does refuse a client viewer
// before asking, which is a departure from README.md's first rule and is argued
// where it is written, in surfaces/Unverified.tsx. It is not argued here,
// because a request that does reach the service is still answered by the
// service's decision and mapped to the same Failure vocabulary as everything
// else.

import type { UnverifiedDomainSortKey, UnverifiedSortKey } from "../../../operator/src/unverifiedAttempts.js";
import { unverifiedAttemptsUrl } from "./endpoints.js";
import { authenticated, failureFor, type ApiOptions, type Read } from "./client.js";
import type { SessionToken } from "../session/store.js";

/**
 * How big a page this console asks for before anybody touches the pager.
 *
 * Twenty five, which is the same number the people list uses and is chosen by
 * the same two arguments: it is one of the three sizes the shared pager offers,
 * so the control and the first request agree from the first render, and the
 * page is the unit `report_access` counts a disclosure in.
 *
 * NOT SMALLER THAN THE PEOPLE LIST, and the temptation to make it ten was real.
 * The operator module refuses that move in as many words. These addresses are a
 * worse disclosure than a client's own attendees, but a narrower page does not
 * make the disclosure smaller: the same reader asks more times, files more
 * audit rows, and reads the same addresses. It only makes the record of what
 * they did harder to read. The ceiling on this read is who may make it.
 *
 * Its own constant rather than the service's `UNVERIFIED_ATTEMPT_PAGE_DEFAULT`,
 * for the reason attendees.ts gives about importing a default: the figure filed
 * against somebody's sign-in would otherwise change without a screen changing.
 */
export const UNVERIFIED_PER_PAGE = 25;

/**
 * How many unproved addresses share a domain at this activation.
 *
 * THE ANSWER THE FEATURE WAS ASKED FOR, and first in this file for the reason
 * it is first in the service's type: the cheap answer should be the one a
 * screen reaches for first. Counted over the whole activation rather than over
 * the page, so it does not move when somebody pages.
 */
export type UnverifiedDomain = {
  readonly domain: string;
  /** Distinct mailboxes, which is how many people it might be. */
  readonly addresses: number;
  /** Times a code was asked for across them, which is how hard they tried. */
  readonly requests: number;
};

export type UnverifiedAttempt = {
  readonly attemptId: string;
  /** Unproved, in cleartext. Nobody demonstrated control of this mailbox. */
  readonly email: string;
  /**
   * Everything after the last `@`, or null where there is none.
   *
   * Kept rather than recomputed in the screen from `email`, and kept even
   * though the address beside it already contains it. Null is the load-bearing
   * case: the tally is computed only over addresses containing an `@`, so a row
   * with no domain is a row the tally above does not count, and a reader adding
   * the tally up and finding it short deserves to be told which rows are
   * missing from it rather than left to find the discrepancy alone.
   */
  readonly domain: string | null;
  readonly firstRequestedAt: string;
  readonly lastRequestedAt: string;
  readonly requestCount: number;
};

export type UnverifiedPagePosition = {
  readonly limit: number;
  readonly offset: number;
  readonly returned: number;
  /** Every unproved address at this activation, not every one on this page. */
  readonly total: number;
  readonly more: boolean;
};

/**
 * When these rows go, and the two facts that decide it.
 *
 * Both halves are nullable and neither null is an error. A null `sealedAt` is
 * an activation whose record is still open, and a null `retentionMonths` is a
 * client with no processing agreement recording one, which is the case
 * migration 130 singles out: no agreement means no second clock, and sealing is
 * then the only thing that removes these addresses. `note` says which case this
 * is in, and it is required precisely because the two nulls are
 * indistinguishable from absent fields to anything except the sentence.
 */
export type UnverifiedRetention = {
  readonly sealedAt: string | null;
  readonly retentionMonths: number | null;
  readonly note: string;
};

export type UnverifiedAttemptPage = {
  readonly activationId: string;
  readonly asAt: string;
  readonly accessNote: string;
  readonly domains: readonly UnverifiedDomain[];
  readonly attempts: readonly UnverifiedAttempt[];
  readonly page: UnverifiedPagePosition;
  readonly retention: UnverifiedRetention;
  readonly subsetNote: string;
  readonly useNote: string;
};

/**
 * One page of the addresses nobody proved.
 *
 * The page bounds are arguments rather than state held here, which is
 * `readAttendees`'s shape and its reason: the pager on the screen and the
 * numbers in the `report_access` row this read files must be the same numbers.
 *
 * The domain tally arrives in this same response and is not a separate request.
 * That is the service's choice and it has a consequence worth stating where
 * somebody might later try to be clever about it: there is no way to read the
 * tally without also receiving the page of addresses, so a screen cannot fetch
 * the cheap half alone. Anything on a screen that hides the addresses is hiding
 * them after they arrived.
 */
export async function readUnverifiedAttempts(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
  offset: number,
  limit: number = UNVERIFIED_PER_PAGE,
  /** The addresses' order and the tally's, as the reader pressed them; null for the service's own. */
  order: { readonly key: UnverifiedSortKey; readonly dir: "asc" | "desc" } | null = null,
  domainOrder: { readonly key: UnverifiedDomainSortKey; readonly dir: "asc" | "desc" } | null = null,
): Promise<Read<UnverifiedAttemptPage>> {
  const answer = await authenticated(options, token, {
    path: unverifiedAttemptsUrl(activationId, limit, offset, order, domainOrder),
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...failureFor(answer) };

  const raw = fields(answer.body);
  const rows = raw["attempts"];
  const position = asPagePosition(raw["page"], limit, offset);
  const retention = asRetention(raw["retention"]);
  const subsetNote = text(raw["subset_note"]);
  const useNote = text(raw["use_note"]);
  const accessNote = text(raw["access_note"]);

  // ALL FOUR SENTENCES ARE REQUIRED, and `use_note` is the one worth naming. It
  // is the sentence saying these mailboxes are unproved and may belong to
  // strangers. A response that lost it would draw a perfectly convincing table
  // of email addresses under a client's name with nothing on the page
  // contradicting the obvious reading of it. Defaulting it to an empty string
  // would be this console deciding the warning is decoration; refusing is the
  // only reading that keeps it load-bearing.
  if (
    !Array.isArray(rows) ||
    position === null ||
    retention === null ||
    subsetNote === null ||
    useNote === null ||
    accessNote === null
  ) {
    return { ok: false, ...unreadable() };
  }

  const domainRows = raw["domains"];
  if (!Array.isArray(domainRows)) return { ok: false, ...unreadable() };
  const domains: UnverifiedDomain[] = [];
  for (const entry of domainRows) {
    const domain = asDomain(entry);
    if (domain === null) return { ok: false, ...unreadable() };
    domains.push(domain);
  }

  const attempts: UnverifiedAttempt[] = [];
  for (const entry of rows) {
    const line = asAttempt(entry);
    if (line === null) return { ok: false, ...unreadable() };
    attempts.push(line);
  }

  return {
    ok: true,
    value: {
      activationId: text(raw["activation_id"]) ?? activationId,
      asAt: text(raw["as_at"]) ?? "",
      accessNote,
      domains,
      attempts,
      page: position,
      retention,
      subsetNote,
      useNote,
    },
  };
}

// ------------------------------------------------------------------ the shapes

/**
 * One line of the tally.
 *
 * Refused rather than repaired on a missing count, unlike the tolerant
 * treatment attendees.ts gives a person's optional fields. A domain drawn with
 * a blank or a zero beside it is a finding, and the finding it draws is that
 * nothing went wrong at that domain.
 *
 * The service's order is kept. It sorts by addresses and then by domain so that
 * two equal counts do not swap places between requests, and re-sorting here
 * would replace an order somebody can rely on with one this file invented.
 */
function asDomain(value: unknown): UnverifiedDomain | null {
  const row = fields(value);
  const domain = text(row["domain"]);
  const addresses = integer(row["addresses"]);
  const requests = integer(row["requests"]);
  if (domain === null || addresses === null || requests === null) return null;
  return { domain, addresses, requests };
}

/**
 * One address.
 *
 * Everything except `domain` is required. A row with no address is not a row
 * about anything, and a row with no moments cannot be placed in the show, which
 * is `asLine`'s argument in attendees.ts. `request_count` joins them here
 * because it is the figure that makes a typo visible: nine goes at one wrong
 * domain drawn as a blank is a row that looks like somebody who tried once.
 */
function asAttempt(value: unknown): UnverifiedAttempt | null {
  const row = fields(value);
  const attemptId = text(row["attempt_id"]);
  const email = text(row["email"]);
  const firstRequestedAt = text(row["first_requested_at"]);
  const lastRequestedAt = text(row["last_requested_at"]);
  const requestCount = integer(row["request_count"]);
  if (
    attemptId === null ||
    email === null ||
    firstRequestedAt === null ||
    lastRequestedAt === null ||
    requestCount === null
  ) {
    return null;
  }
  return { attemptId, email, domain: text(row["domain"]), firstRequestedAt, lastRequestedAt, requestCount };
}

function asPagePosition(value: unknown, limit: number, offset: number): UnverifiedPagePosition | null {
  const row = fields(value);
  const returned = integer(row["returned"]);
  const total = integer(row["total"]);
  // `total` is what the pager counts in and what tells a reader this page is a
  // slice. Without it there is no honest pager, so there is no page.
  if (returned === null || total === null) return null;
  return {
    limit: integer(row["limit"]) ?? limit,
    offset: integer(row["offset"]) ?? offset,
    returned,
    total,
    more: row["more"] === true,
  };
}

/**
 * The two clocks, or the fact that the response did not describe them.
 *
 * `note` required, the two facts not. That split is the whole point of the
 * block: the service holds both facts as nullable columns and writes a sentence
 * saying what the pair amounts to, so the sentence is the answer and the two
 * values are the working. A response carrying the values without the sentence
 * would leave this console to decide what a null retention means, which is
 * exactly the judgement migration 130 wrote a paragraph to keep from being made
 * twice in two places.
 */
function asRetention(value: unknown): UnverifiedRetention | null {
  const row = fields(value);
  const note = text(row["note"]);
  if (note === null) return null;
  return { sealedAt: text(row["sealed_at"]), retentionMonths: integer(row["retention_months"]), note };
}

// ------------------------------------------------------------- small things

/**
 * The refusal for an answer this file could not read.
 *
 * One wording rather than attendees.ts's `what` argument, because there is one
 * endpoint here. The second sentence is this read's own and is not borrowed: a
 * partly understood answer about people who never agreed to be written down is
 * worse than none, and the specific failure to avoid is drawing the addresses
 * while having failed to read the sentence that says what they are.
 */
function unreadable(): { readonly kind: "unavailable"; readonly detail: string } {
  return {
    kind: "unavailable",
    detail:
      "The service answered with unverified attempts this console could not read. Nothing is drawn from " +
      "it. An address on this list means nothing without the sentences the service sends beside it, so a " +
      "response missing any of them is not drawn in part.",
  };
}

function fields(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** A string, or nothing. Never the empty string, which a screen reads as a value. */
function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * A whole number the service actually sent.
 *
 * `typeof value === "number"` first, for the reason attendees.ts gives:
 * `Number(null)` is zero and finite, so the shorter version turns an absent
 * count into a report that nothing happened. Every figure on this screen is one
 * somebody would act on, and a domain reading zero is a domain nobody looks at.
 */
function integer(value: unknown): number | null {
  if (typeof value === "number") return Number.isSafeInteger(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}
